import React, { useState } from 'react';
import { func, shape } from 'prop-types';
import { connect } from 'react-redux';
import { NavLink } from 'react-router-dom';

import { useFormik } from 'formik';
import * as Yup from 'yup';

import { Auth } from 'aws-amplify';
import { getError } from '../../helpers/awsErrorMessages';

import Button from '@material-ui/core/Button';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import Input from '@material-ui/core/Input';
import InputAdornment from '@material-ui/core/InputAdornment';
import InputLabel from '@material-ui/core/InputLabel';
import Paper from '@material-ui/core/Paper';
import BobPhoneInput from '../../components/BobPhoneInput';
import PasswordRequirements from '../../components/PasswordRequirements';
import TermsConditions from '../../components/Terms/TermsAndConditions';

import { enqueueSnackbar } from '../../actions/applicationMessageActions';

import classes from './AuthCore.module.css';

const ValidationSchema = Yup.object().shape({
    email: Yup.string()
        .email('Must be an email address')
        .required('Email address required'),
    password: Yup.string()
        .min(8)
        .max(99)
        .matches(
            // eslint-disable-next-line no-useless-escape
            /^(?=[0-9a-zA-Z\^\$\*\.\[\]\{\}\(\)\?\-\"\!\@\#\%\&\/\\\,\>\<\'\:\;\|\_\~\`]{6,}$)(?=[^a-z]*[a-z])(?=[^A-Z]*[A-Z])(?=[^0-9]*[0-9])(?=[^\^\$\*\.\[\]\{\}\(\)\?\-\"\!\@\#\%\&\/\\\,\>\<\'\:\;\|\_\~\`]*[\^\$\*\.\[\]\{\}\(\)\?\-\"\!\@\#\%\&\/\\\,\>\<\'\:\;\|\_\~\`]).*/,
            'Password does not match required format.'
        )
        .required('Password required'),
    confirmPassword: Yup.string()
        .oneOf([Yup.ref('password'), null], 'Passwords must match')
        .required('Password confirmation required'),
    firstName: Yup.string()
        .required('First name is required')
        .matches(
            /^[a-z ,.'-]+$/i,
            'First Name should contain only strings'
        ),
    lastName: Yup.string()
        .required('Last name is required').matches(
            /^[a-z ,.'-]+$/i,
            'Last Name should contain only strings'
        ),
    phone: Yup.string()
        .required('Phone number is required')
});

const SignUp = props => {
    const formik = useFormik({
        initialValues: {
            email:           '',
            password:        '',
            confirmPassword: '',
            firstName:       '',
            lastName:        '',
            phone:           ''
        },
        validationSchema: ValidationSchema,
        onSubmit:         async (values, { setSubmitting }) => {
            if (phoneCountry && phoneCountry.dialCode === values.phone) {
                values.phone = '';
            }
            setSubmitting(true);

            const lowerCaseEmail = values.email.toLowerCase();
            const awsFriendlyPhone = makeNumberAwsFriendly(values.phone);

            try {
                await props.signUp({
                    username:   lowerCaseEmail,
                    password:   values.password,
                    attributes: {
                        email:        lowerCaseEmail,
                        phone_number: awsFriendlyPhone,
                        given_name:   values.firstName,
                        family_name:  values.lastName
                    }
                });

                setShowFollowUp(true);
            } catch (error) {
                const errorMessage = getError(error);
                props.enqueueSnackbar(errorMessage, 'error');
                setSubmitting(false);
            }
        }
    });

    const [showFollowUp, setShowFollowUp] = useState(false);
    const [tocModalOpen, setTocModalOpen] = useState(false);
    const [popperOpen, setPopperOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState();
    const [phoneCountry, setPhoneCountry] = useState();

    const makeNumberAwsFriendly = number => {
        if (number.startsWith('+')) {
            return number;
        }

        return `+${number}`;
    };

    const handleBackButton = event => {
        event.preventDefault();

        props.history.push('/signIn');
    };

    const handlePhoneNoChange = (value) => {
        formik.setFieldValue('phone', value, false);
    };


    const renderForm = () => {
        return (
            <React.Fragment>
                <h3>Sign Up</h3>
                <form
                    onSubmit={formik.handleSubmit}
                    className={classes.form}
                >
                    <FormControl className={classes.formControl}>
                        <InputLabel
                            htmlFor="firstName"
                            classes={{
                                error:   classes.error,
                                focused: classes.labelFocused,
                                root:    classes.label
                            }}
                            error={Boolean(formik.errors.firstName) && Boolean(formik.touched.firstName)}
                        >
                            First Name
                        </InputLabel>
                        <Input
                            id="firstName"
                            name="firstName"
                            value={formik.values.firstName}
                            onChange={formik.handleChange}
                            classes={{
                                error:     classes.cssErrorUnderline,
                                underline: classes.cssUnderline,
                                root:      classes.input
                            }}
                            error={Boolean(formik.errors.firstName) && Boolean(formik.touched.firstName)}
                            inputProps={{
                                onBlur: formik.handleBlur
                            }}
                        />
                        {formik.touched.firstName &&
                         formik.errors.firstName && <FormHelperText className={classes.error}>{formik.errors.firstName}</FormHelperText>}
                    </FormControl>
                    <FormControl className={classes.formControl}>
                        <InputLabel
                            htmlFor="lastName"
                            classes={{
                                error:   classes.error,
                                focused: classes.labelFocused,
                                root:    classes.label
                            }}
                            error={Boolean(formik.errors.lastName) && Boolean(formik.touched.lastName)}
                        >
                            Last Name
                        </InputLabel>
                        <Input
                            id="lastName"
                            name="lastName"
                            value={formik.values.lastName}
                            onChange={formik.handleChange}
                            classes={{
                                error:     classes.cssErrorUnderline,
                                underline: classes.cssUnderline,
                                root:      classes.input
                            }}
                            error={Boolean(formik.errors.lastName) && Boolean(formik.touched.lastName)}
                            inputProps={{
                                onBlur: formik.handleBlur
                            }}
                        />
                        {formik.touched.lastName &&
                         formik.errors.lastName && <FormHelperText className={classes.error}>{formik.errors.lastName}</FormHelperText>}
                    </FormControl>
                    <FormControl className={classes.formControl}>
                        <InputLabel
                            htmlFor="email"
                            classes={{
                                error:   classes.error,
                                focused: classes.labelFocused,
                                root:    classes.label
                            }}
                            error={Boolean(formik.errors.email) && Boolean(formik.touched.email)}
                        >
                            Email
                        </InputLabel>
                        <Input
                            id="email"
                            name="email"
                            value={formik.values.email}
                            onChange={formik.handleChange}
                            classes={{
                                error:     classes.cssErrorUnderline,
                                underline: classes.cssUnderline,
                                root:      classes.input
                            }}
                            error={Boolean(formik.errors.email) && Boolean(formik.touched.email)}
                            inputProps={{
                                onBlur: formik.handleBlur
                            }}
                        />
                        {formik.touched.email &&
                         formik.errors.email && <FormHelperText className={classes.error}>{formik.errors.email}</FormHelperText>}
                    </FormControl>
                    <FormControl className={classes.formControl}>
                        <InputLabel
                            htmlFor="phone"
                            classes={{
                                error:   classes.error,
                                focused: classes.labelFocused,
                                root:    classes.label
                            }}
                            error={Boolean(formik.errors.phone) && Boolean(formik.touched.phone)}
                        >
                            Phone Number
                        </InputLabel>
                        <BobPhoneInput
                            id="phone"
                            name="phone"
                            value={formik.values.phone}
                            onChange={
                                (v, d, e, fv) => {
                                    setPhoneCountry(d);
                                    handlePhoneNoChange(v);
                                }
                            }
                            classes={{
                                error:     classes.cssErrorUnderline,
                                underline: classes.cssUnderline,
                                root:      classes.input
                            }}
                            error={Boolean(formik.errors.phone) && Boolean(formik.touched.phone)}
                            inputProps={{
                                onBlur: formik.handleBlur
                            }}
                        />
                        {formik.touched.phone &&
                         formik.errors.phone && <FormHelperText className={classes.error}>{formik.errors.phone}</FormHelperText>}
                    </FormControl>
                    <FormControl className={classes.formControl}>
                        <InputLabel
                            htmlFor="password"
                            classes={{
                                error:   classes.error,
                                focused: classes.labelFocused,
                                root:    classes.label
                            }}
                            error={Boolean(formik.errors.password) && Boolean(formik.touched.password)}
                        >
                            Password
                        </InputLabel>
                        <Input
                            id="password"
                            name="password"
                            value={formik.values.password}
                            onChange={formik.handleChange}
                            type="password"
                            classes={{
                                error:     classes.cssErrorUnderline,
                                underline: classes.cssUnderline,
                                root:      classes.input
                            }}
                            error={Boolean(formik.errors.password) && Boolean(formik.touched.password)}
                            endAdornment={
                                <InputAdornment
                                    onClick={event => {
                                        setPopperOpen(!popperOpen);
                                        setAnchorEl(event.currentTarget);
                                    }}
                                    position="end"
                                >
                                    <i className="fas fa-question-circle" />
                                </InputAdornment>
                            }
                            inputProps={{
                                onBlur: formik.handleBlur
                            }}
                        />
                        {formik.touched.password &&
                         formik.errors.password && <FormHelperText className={classes.error}>{formik.errors.password}</FormHelperText>}
                    </FormControl>
                    <FormControl className={classes.formControl}>
                        <InputLabel
                            htmlFor="confirmPassword"
                            classes={{
                                error:   classes.error,
                                focused: classes.labelFocused,
                                root:    classes.label
                            }}
                            error={Boolean(formik.errors.confirmPassword) && Boolean(formik.touched.confirmPassword)}
                        >
                            Confirm Password
                        </InputLabel>
                        <Input
                            id="confirmPassword"
                            name="confirmPassword"
                            value={formik.values.confirmPassword}
                            onChange={formik.handleChange}
                            type="password"
                            classes={{
                                error:     classes.cssErrorUnderline,
                                underline: classes.cssUnderline,
                                root:      classes.input
                            }}
                            error={Boolean(formik.errors.confirmPassword) && Boolean(formik.touched.confirmPassword)}
                            inputProps={{
                                onBlur: formik.handleBlur
                            }}
                        />
                        {formik.touched.confirmPassword &&
                         formik.errors.confirmPassword && <FormHelperText className={classes.error}>{formik.errors.confirmPassword}</FormHelperText>}
                    </FormControl>
                    <PasswordRequirements
                        open={popperOpen}
                        anchorEl={anchorEl}
                        placement="left"
                    />
                    <div className={classes.buttonContainer}>
                        <Button
                            variant="contained"
                            color="primary"
                            type="submit"
                            disabled={formik.isSubmitting}
                            className={classes.primaryFullWidth}
                        >
                            Sign Up
                        </Button>
                    </div>
                </form>
                <div className={classes.termsAndConditionsContainer}>
                    <p>
                        By signing up you agree to the WennSoft&reg;
                    </p>
                    <button
                        className={classes.tocButton}
                        onClick={() => setTocModalOpen(true)}
                    >
                        Terms and Conditions
                    </button>
                    <TermsConditions open={tocModalOpen} handleClose={() => setTocModalOpen(false)} />
                </div>
            </React.Fragment>
        );
    };

    const renderFollowUp = () => {
        return (
            <React.Fragment>
                <div className={classes.requestReceivedContainer}>
                    <h3>Your Request Was Received</h3>
                    <p>Thank you for signing up for a WennSoft account, your request is currently being reviewed. You will receive a notification when your request is approved.</p>
                    <p>Already approved?
                        <NavLink
                            exact to="/signin"
                            className={classes.signInLink}
                        >
                            Sign in here
                        </NavLink>
                    </p>
                </div>
            </React.Fragment>
        );
    };

    return (
        <div className={classes.AuthCore}>
            <Paper className={classes.AuthCoreContainer}>
                <div className={classes.AuthCoreHeroContainer} />
                <div className={classes.AuthCoreFormContainer}>
                    <div className={classes.AuthCoreHeader}>
                        <button onClick={handleBackButton}>
                            <img src="/assets/images/backButtonColorIcon.svg" alt="back arrow" />
                            Back to Sign In
                        </button>
                        <img
                            alt="BOB Logo"
                            className={classes.BOBLogoHeader}
                            src="/assets/images/BOB-BLACK.png"
                        />
                    </div>
                    {!showFollowUp ? renderForm() : renderFollowUp()}
                </div>
            </Paper>
        </div>
    );
};

SignUp.defaultProps = {
    enqueueSnackbar: () => {},
    signUp:          Auth.signUp.bind(Auth)
};

SignUp.propTypes = {
    enqueueSnackbar: func,
    history:         shape({
        replace: func.isRequired
    })
};

const mapDispatchToProps = {
    enqueueSnackbar
};

export default connect(null, mapDispatchToProps)(SignUp);
